import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import swal from "sweetalert";
import { useSelector, useDispatch } from "react-redux";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import APIs from "../../Constants/apis";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import axiosInstance from "../../config/axiosApi/axiosApi";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import CateringServiceModal from "./cateringServiceModal";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import {
  applyCatalogTotalFromFetch,
  applyCatalogTotalFromMutation,
} from "../../utils/catalogTotalRecords";


import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import ErrorBoundary from "../../components/CustomComponents/ErrorBoundary/ErrorBoundary.jsx";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";

const SERVICE_SORT_FIELDS = {
  serviceId: "CateringService_ID",
  serviceName: "ServiceName",
  price: "Price",
  createdAt: "CreatedDateTime",
};

const SERVICE_SORT_LABELS = {
  [SERVICE_SORT_FIELDS.serviceId]: "Service ID",
  [SERVICE_SORT_FIELDS.serviceName]: "Service Name",
  [SERVICE_SORT_FIELDS.price]: "Price",
  [SERVICE_SORT_FIELDS.createdAt]: "Created At",
};

const normalizeService = (row) => ({
  cateringService_ID: row.cateringService_ID ?? row.CateringService_ID,
  cateringService_Guid: row.cateringService_Guid ?? row.CateringService_Guid ?? "",
  serviceName: row.serviceName ?? row.ServiceName ?? "",
  serviceDescription: row.serviceDescription ?? row.ServiceDescription ?? "",
  price: Number(row.price ?? row.Price ?? 0),
  isActive: row.isActive ?? row.IsActive ?? true,
  createdDateTime: row.createdDateTime ?? row.CreatedDateTime,
  updatedDateTime: row.updatedDateTime ?? row.UpdatedDateTime,
});

const formatServiceId = (id) => {
  const num = Number(id);
  if (!Number.isFinite(num) || num <= 0) return "—";
  return `#${String(num).padStart(5, "0")}`;
};

const formatCreatedAt = (dateVal) => {
  if (!dateVal) return "—";
  try {
    const d = new Date(dateVal);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

const ManageCateringServices = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const [search, setSearch] = useState("");
  const pageKey = "cateringServices";
  const defaultVisibleColumns = {
    serviceId: true,
    price: true,
    description: true,
    createdAt: true,
  };
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: SERVICE_SORT_FIELDS.createdAt, label: "Created At" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;
  const dispatch = useDispatch();

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  const currencySymbols = { GBP: "£", USD: "$", EUR: "€" };
  const formatPrice = (amount) =>
    `${currencySymbols[compCurrency] || compCurrency || "£"} ${Number(amount || 0).toFixed(2)}`;

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const servicesFetchSeqRef = useRef(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [serviceToEdit, setServiceToEdit] = useState(null);

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const clearFilters = () => setSearch("");

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: SERVICE_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const openCreateModal = () => {
    setServiceToEdit(null);
    setIsModalOpen(true);
  };

  const handleEdit = (service) => {
    setServiceToEdit(service);
    setIsModalOpen(true);
  };

  const handleServiceSaved = () => {
    fetchServices();
  };

  const DeleteService = async (service) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete "${service.serviceName}"?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) return;

    setLoading(true);
    try {
      const response = await axiosInstance.delete(
        APIs.DELETE_CATERING_SERVICE(service.cateringService_ID),
        { params: { CompID } }
      );

      if (response.data?.status) {
        setServices((prev) => {
          const next = prev.filter(
            (s) => s.cateringService_ID !== service.cateringService_ID
          );
          if (!search.trim()) applyCatalogTotalFromMutation(setCatalogTotalRecords, next.length);
          return next;
        });
        showSuccessToast("Service deleted successfully.");
      } else {
        showErrorToast(response.data?.message || "Delete failed.");
      }
    } catch {
      showErrorToast("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = useCallback(async () => {
    const fetchSeq = ++servicesFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_CATERING_SERVICES, {
        params: {
          CompID,
          Search: search?.trim() || "",
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
        },
      });

      if (fetchSeq !== servicesFetchSeqRef.current) return;

      const raw = response.data?.data ?? response.data;
      const list = (Array.isArray(raw) ? raw : []).map(normalizeService);
      setServices(list);

      if (!search.trim()) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, list.length);
      }
    } catch {
      if (fetchSeq !== servicesFetchSeqRef.current) return;
      setServices([]);
      showErrorToast("Failed to load services.");
    } finally {
      if (fetchSeq === servicesFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [CompID, search, sortBy.value, sortOrder.value]);

  const totalRecords = services.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / Number(pageSize.value)));
  const pagedServices = useMemo(() => {
    const size = Number(pageSize.value);
    const start = (pageNumber - 1) * size;
    return services.slice(start, start + size);
  }, [services, pageNumber, pageSize]);

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
      dispatch(
        setVisibleColumns({
          pageKey,
          columns: mergeVisibleColumns(
            preferences.visibleColumns,
            defaultVisibleColumns
          ),
        })
      );
    }
    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(servicesFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchServices(), [fetchServices]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy?.value, sortOrder?.value]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const hasFiltersApplied = () => search.trim() !== "";
  const showEmptyCatalogState =
    initialLoadComplete && !loading && !hasFiltersApplied() && catalogTotalRecords === 0;
  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No services match your search "${search.trim()}". Try different keywords or clear filters.`;
    }
    return "There are no services yet. Create your first catering service package.";
  };

  const renderNoDataMessage = () => {
    if (loading || services.length > 0) return null;
    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No services found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <ErrorBoundary>
      <div>
        <CateringServiceModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          onServiceSaved={handleServiceSaved}
          compID={CompID}
          serviceToEdit={serviceToEdit}
        />

        <div className="d-flex align-items-center mb-3">
          <div>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="#/">Home</a>
              </li>
              <li className="breadcrumb-item">
                <a href="#/">Catering</a>
              </li>
              <li className="breadcrumb-item active">Services</li>
            </ol>
            <h1 className="page-header mb-0">Services</h1>
          </div>
          <div className="ms-auto">
            <button
              type="button"
              className="btn px-4"
              onClick={openCreateModal}
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: "none",
                color: "white",
                borderRadius: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "600",
                transition: "all 0.3s ease",
                boxShadow: `0 2px 8px ${themeColor}40`,
              }}
            >
              <i className="fa fa-plus fa-lg me-2 ms-n2 text-white" />
              Add Service
            </button>
          </div>
        </div>

        <div className="mb-3 mt-2">
          {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

          {showEmptyCatalogState && (
            <AddButton onClick={openCreateModal} title="Add Service" />
          )}

          {showGridToolbar && (
            <ModernGridToolbar
              isDarkMode={isDarkMode}
              title="Services"
              totalCount={totalRecords}
              searchValue={search}
              onSearchChange={setSearch}
              searchPlaceholder="Service, price, service id..."
              bodyFlush
              headerControls={
                <ColumnSelection
                  visibleColumns={visibleColumns}
                  setVisibleColumns={(cols) =>
                    dispatch(setVisibleColumns({ pageKey, columns: cols }))
                  }
                />
              }
            >
              <div className="px-3 pb-3">
                <GridSectionLoader loading={loading} minHeight="280px">
                  {renderNoDataMessage()}

                  {!loading && pagedServices.length > 0 && (
                    <div
                      className="table-responsive table-container"
                      style={{
                        minHeight: "200px",
                        maxHeight: "450px",
                        overflowY: "auto",
                        overflowX: "auto",
                      }}
                    >
                      <table
                        className="table table-hover"
                        style={{ "--modern-grid-sort-active": themeColor }}
                      >
                        <thead
                          className="bg-light sticky-header"
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 10000,
                            backgroundColor: isDarkMode ? "#2d3436" : themeColor,
                            color: "#ffffff",
                          }}
                        >
                          <tr>
                            {visibleColumns.serviceId && (
                              <SortableColumnHeader
                                sortField={SERVICE_SORT_FIELDS.serviceId}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleColumnSort}
                              >
                                Service ID
                              </SortableColumnHeader>
                            )}
                            <SortableColumnHeader
                              sortField={SERVICE_SORT_FIELDS.serviceName}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              Service Name
                            </SortableColumnHeader>
                            {visibleColumns.price && (
                              <SortableColumnHeader
                                sortField={SERVICE_SORT_FIELDS.price}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleColumnSort}
                                className="text-end"
                              >
                                Price
                              </SortableColumnHeader>
                            )}
                            {visibleColumns.description && (
                              <th className="pt-2 pb-2">Description</th>
                            )}
                            {visibleColumns.createdAt && (
                              <SortableColumnHeader
                                sortField={SERVICE_SORT_FIELDS.createdAt}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleColumnSort}
                              >
                                Created At
                              </SortableColumnHeader>
                            )}
                            <th className="pt-2 pb-2 text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedServices.map((service) => (
                            <tr key={service.cateringService_ID}>
                              {visibleColumns.serviceId && (
                                <td
                                  className="align-middle text-muted"
                                  onClick={() => handleEdit(service)}
                                  style={{ cursor: "pointer" }}
                                >
                                  {formatServiceId(service.cateringService_ID)}
                                </td>
                              )}
                              <td
                                className="align-middle"
                                onClick={() => handleEdit(service)}
                                style={{ cursor: "pointer" }}
                              >
                                {service.serviceName}
                              </td>
                              {visibleColumns.price && (
                                <td
                                  className="align-middle text-end"
                                  onClick={() => handleEdit(service)}
                                  style={{ cursor: "pointer" }}
                                >
                                  {formatPrice(service.price)}
                                </td>
                              )}
                              {visibleColumns.description && (
                                <td
                                  className="align-middle text-muted"
                                  onClick={() => handleEdit(service)}
                                  style={{ cursor: "pointer", maxWidth: "280px" }}
                                >
                                  {service.serviceDescription || "—"}
                                </td>
                              )}
                              {visibleColumns.createdAt && (
                                <td
                                  className="align-middle"
                                  onClick={() => handleEdit(service)}
                                  style={{ cursor: "pointer" }}
                                >
                                  {formatCreatedAt(service.createdDateTime)}
                                </td>
                              )}
                              <td className="align-middle text-center">
                                <TableActionsMenu menuStyle={{ zIndex: 9999 }}>
                                  <TableActionsItem
                                    icon="fa fa-edit"
                                    onClick={() => handleEdit(service)}
                                  >
                                    Edit
                                  </TableActionsItem>
                                  <TableActionsItem
                                    danger
                                    icon="fa fa-trash"
                                    onClick={() => DeleteService(service)}
                                  >
                                    Delete
                                  </TableActionsItem>
                                </TableActionsMenu>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <ModernGridPagerFooter
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    totalRecords={totalRecords}
                    pageNumber={pageNumber}
                    totalPages={totalPages}
                    setPageNumber={setPageNumber}
                  />
                </GridSectionLoader>
              </div>
            </ModernGridToolbar>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default ManageCateringServices;
